import { Injectable } from '@angular/core';
import {
  AlertController,
  LoadingController,
  ToastController,
} from 'ionic-angular';
import { UserProvider } from '../user/user';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ToolsProvider } from '../tools/tools';
import { EnvVariables } from '../../environments/environment';
import { EventEmitter } from '../../includes/eventEmitter';
import { Facebook } from '@ionic-native/facebook';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/toPromise';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Capacitor, Plugins } from '@capacitor/core';
import { OnboardingProvider } from '../onboarding/onboarding';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';

@Injectable()
export class AuthProvider extends EventEmitter {
  log = debug('app:AuthProvider');

  static authKeyID = 'APP_AUTH_KEY';

  pushToken = '';
  checkingAuth; // promise or null
  creatingUnauthedUser;
  loader = null;
  enteredCorrectAuthCode = false;

  settings = {
    autoGenerateUnauthedUser: false,
  };

  constructor(
    public crudApi: CrudApiProvider,
    public alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    public tools: ToolsProvider,
    public env: EnvVariables,
    private fb: Facebook,
    public user: UserProvider,
    public toastCtrl: ToastController,
    public onboarding: OnboardingProvider
  ) {
    super();
    this.checkAuthKey();
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') GoogleAuth.initialize();
  }

  isAuthenticated(createUnauthed?) {
    if (createUnauthed) {
      this.settings.autoGenerateUnauthedUser = true;
    }
    if (this.checkingAuth) {
      return this.checkingAuth;
    }

    return new Promise((resolve, reject) => {
      this.crudApi
        .get('/api/users/me')
        .then(res => {
          this.setAuthenticated(res.data);
          this.log('AUTHED', this.user.get());
          resolve((this.user.authenticated = !!this.user.get('_id')));
        })
        .catch(e => {
          if (
            _.get(e, 'msg') === 'unauthorized' &&
            _.get(e, 'source') === 'turnkey' &&
            this.settings.autoGenerateUnauthedUser
          ) {
            this.createUnauthedAccount()
              .then(res => {
                this.emit('authenticated');
                resolve((this.user.authenticated = !!this.user.get('_id')));
              })
              .catch(createErr => {
                console.error('AUTH ERROR', e);
                reject(e);
              });
          } else {
            console.error('AUTH ERROR', e);
            reject(e);
          }
        });
    });
  }

  private setAuthenticated(user) {
    this.user.updateUser(user);
    this.user.setJobFilterProfileDefaults();
    this.user.authenticated = true;
    this.user.reloadFriends();
    if (this.user.get('states.isUnauthedUser')) {
      this.user.unauthedUser = true;
      this.log('UNAUTHED USER');
    } else {
      this.user.unauthedUser = false;
    }
    this.setAid();
    this.emit('authenticated');
  }

  setAid() {
    try {
      // interfaces with our AID authentication
      let existing: any = {};
      try {
        existing = JSON.parse(localStorage.getItem('AID')) || {};
      } catch (e) {}

      existing.mid = this.user.get('_id');
      let zip = this.user.get('jobSearchInfo.zip');

      if (zip) {
        existing.location = zip;
      }

      localStorage.setItem('AID', JSON.stringify(existing));
    } catch (e) {
      console.error('aid error', e);
    }
  }

  defaultLogin(email: string, password: string, loading = true) {
    this.setLoading();

    return this.crudApi
      .post('/turnkey/login', { email: email, password: password })
      .then(
        res => {
          const authKey = res.data;

          CrudApiProvider.authKey = authKey;

          this.setStorage(AuthProvider.authKeyID, authKey);

          return this.crudApi.get('/api/users/me').then(res2 => {
            this.setAuthenticated(res2.data);
            this.setLoading();

            return;
          });
        },
        e => {
          this.setLoading();
          return Promise.reject(e);
        }
      );
  }

  async loginWithAuthCode(authCode) {
    await this.crudApi.get(`/turnkey/codeAuth/${authCode}`).then(
      login => {
        if (login.data != 'Invalid code') {
          this.enteredCorrectAuthCode = true;
          // return false;

          const authKey = login.data;

          CrudApiProvider.authKey = authKey;

          this.setStorage(AuthProvider.authKeyID, authKey);

          return this.crudApi.get('/api/users/me').then(res2 => {
            this.setAuthenticated(res2.data);
            // this.setLoading();

            // this.onboarding.complete();

            return;
          });
        } else {
          this.enteredCorrectAuthCode = false;
        }
      },
      e => {
        console.log('=== verifiy code error === ', e);
        return false;
      }
    );

    return this.enteredCorrectAuthCode;
  }

  loginWithFacebook() {
    const loader = this.loadingCtrl.create();
    loader.present();

    return this.fb.login(['email']).then(
      res => {
        return this.fb.api('/me?fields=id,name,email,picture', []).then(
          me => {
            const userid = _.get(res, 'authResponse.userID');

            const split = String(me.name || '').split(' '),
              firstName = split.shift(),
              lastName = split.join(' ');

            return this.crudApi
              .post('/turnkey/socialAuth', {
                auth: {
                  network: 'facebook',
                  id: userid,
                  token: _.get(res, 'authResponse.accessToken'),
                  clientId: '262943864465434',
                  expiration: moment().add(100, 'years').toISOString(),
                },
                create: {
                  email: me.email,
                  firstName: firstName,
                  lastName: lastName,
                  userImage: `https://graph.facebook.com/${userid}/picture?type=large`,
                },
              })
              .then(
                res => {
                  CrudApiProvider.authKey = res.data;
                  this.setStorage(AuthProvider.authKeyID, res.data);

                  try {
                    this.tools.logAnalyticsEvent(
                      'track',
                      'CompleteRegistration'
                    );
                  } catch (e) {}

                  return this.crudApi
                    .get('/api/users/me')
                    .then(res2 => {
                      this.user.updateUser(res2.data);
                      if (res2.data) {
                        this.checkingAuth = null;
                        this.user.authenticated = true;
                        this.user.unauthedUser = false;
                        this.tools.emit('closePreviewModal');
                        this.emit('authenticated');

                        this.log(
                          'flowUser',
                          this.onboarding.getNavData('flowData.user')
                        );

                        if (
                          this.onboarding.getNavData('flowData.user') &&
                          this.user.isNewUser()
                        ) {
                          this.user.crudUpdate(
                            this.onboarding.getNavData('flowData.user')
                          );
                        } else {
                        }
                      }
                      if (loader) {
                        loader.dismiss();
                      }
                      return true;
                    })
                    .catch(e => {
                      return Promise.resolve(false);
                    });
                },
                e => {
                  loader.dismiss();
                  this.facebookErrorToast({
                    message:
                      'Facebook is not sharing your email. Please sign up with email.',
                  });
                }
              );
          },
          e => {
            loader.dismiss();
          }
        );
      },
      e => {
        loader.dismiss();
      }
    );
  }

  async loginWithGoogle(): Promise<boolean> {
    const googleRes = await GoogleAuth.signIn();
    this.log('google user', googleRes);

    try {
      let { data } = await this.crudApi.post('/turnkey/socialAuth', {
        auth: {
          network: 'google-firebase',
          id: googleRes.id,
          token: googleRes.authentication.idToken,
          clientId: 'mobile.cdllife.com',
          expiration: moment().add(100, 'years').toISOString(),
        },
        create: {
          email: _.get(googleRes, 'email'),
          firstName: _.get(googleRes, 'givenName'),
          lastName: _.get(googleRes, 'familyName'),
        },
      });

      this.log('turnkey social', data);

      if (!data) {
        this.log(`missing data in response`);
        return false;
      }

      CrudApiProvider.authKey = data;
      this.setStorage(AuthProvider.authKeyID, data);

      try {
        this.tools.logAnalyticsEvent('track', 'CompleteRegistration');
      } catch (e) {}

      const res2 = await this.crudApi.get('/api/users/me');

      if (res2.data) {
        this.user.updateUser(res2.data);
        this.checkingAuth = null;
        this.user.authenticated = true;
        this.user.unauthedUser = false;
        this.tools.emit('closePreviewModal');
        this.emit('authenticated');

        if (
          this.onboarding.getNavData('flowData.user') &&
          this.user.isNewUser()
        ) {
          this.user.crudUpdate(this.onboarding.getNavData('flowData.user'));
        }
        this.log('res2: ', res2);
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

  async loginWithApple(): Promise<boolean> {
    // SignInWithApple returns an object containing Token, user,
    // On the first call (on the phone), it will include the users givenName,
    // Family Name and Email Address
    const res = await SignInWithApple.authorize();

    this.log('SignInWithApple | res', res);

    if (res.response && res.response.identityToken) {
      // Prepare User Object
      let userResponse = {};
      if (res.response.email) {
        _.set(userResponse, 'email', res.response.email);
      }
      if (res.response.givenName) {
        _.set(userResponse, 'firstName', res.response.givenName);
      }
      if (res.response.familyName) {
        _.set(userResponse, 'lastName', res.response.familyName);
      }

      let { data } = await this.crudApi.post('/turnkey/socialAuth', {
        auth: {
          network: 'apple',
          id: res.response.user,
          token: res.response.identityToken,
          clientId: 'mobile.cdllife.com',
          expiration: moment().add(100, 'years').toISOString(),
        },
        create: userResponse,
      });
      if (!data) {
        this.log(`missing data in response`);
        return Promise.resolve(false);
      }
      CrudApiProvider.authKey = data;
      this.setStorage(AuthProvider.authKeyID, data);
      try {
        this.tools.logAnalyticsEvent('track', 'CompleteRegistration');
      } catch (e) {}
      const res2 = await this.crudApi.get('/api/users/me');
      if (res2.data) {
        this.user.updateUser(res2.data);
        this.checkingAuth = null;
        this.user.authenticated = true;
        this.user.unauthedUser = false;
        this.emit('authenticated');

        if (
          this.onboarding.getNavData('flowData.user') &&
          this.user.isNewUser()
        ) {
          this.user.crudUpdate(this.onboarding.getNavData('flowData.user'));
        }
        this.log('res2: ', res2);

        return Promise.resolve(true);
      } else {
        Promise.resolve(false);
      }
    } else {
      Promise.resolve(false);
    }
  }

  logout() {
    const token = this.pushToken || '';

    let p;

    if (token) {
      p = this.crudApi.delete(
        '/api/users/' + this.user.get('_id') + '/deviceTokens/' + token
      );
    } else {
      p = Promise.resolve();
    }

    p.then(
      () => {
        window.location.href = window.location.pathname;
      },
      () => {
        window.location.href = window.location.pathname;
      }
    );

    CrudApiProvider.authKey = undefined;
    this.removeStorage(AuthProvider.authKeyID);
    this.emit('logout');
    this.checkingAuth = null;
    this.user.updateUser({});

    return p;
  }

  createAccount(object) {
    this.setLoading();

    return this.crudApi.post('/api/users', object).then(
      res => {
        this.tools.logAnalyticsEvent('track', 'CompleteRegistration');

        return this.defaultLogin(object.email, object.password, false).then(
          res => {
            this.setLoading();
            return res;
          }
        );
      },
      e => {
        if (e && e.code === 11000) {
          if (/\$username/.test(e.errmsg)) {
            e = 'An account with that username already exists.';
          } else {
            e = 'An account with that email already exists.';
          }
        }

        e = (e && e.msg) || (e && e.error) || e;

        if (typeof e === 'object') {
          e = 'An unknown error occured created your account.';
        }

        this.setLoading();
        this.tools.defaultAlert(e);
        return Promise.reject(e);
      }
    );
  }

  private createUnauthedAccount() {
    if (this.creatingUnauthedUser) {
      return this.creatingUnauthedUser;
    }

    const timestamp = moment().format('X');
    const unauthedUser = {
      email: `ua-${timestamp}@unauthed.cdllife.com`,
      username: `ua-${timestamp}`,
      password: `${timestamp}` + Math.random(),
      unauthed: true,
    };

    return (this.creatingUnauthedUser = this.createAccount(unauthedUser));
  }

  deleteAccount() {
    this.log('DELETING USER', this.user.get('_id'));

    try {
      this.tools.logAnalyticsEvent('trackCustom', 'deleted-account');
    } catch (e) {}
    this.crudApi
      .delete('/api/users/' + this.user.get('_id'))
      .then(res => {
        this.logout();
      })
      .catch(e => {
        this.logout();
      });
  }

  // AUTH

  private async checkAuthKey() {
    if (CrudApiProvider.authKey) return;
    const key = this.getStorage(AuthProvider.authKeyID);
    if (key) {
      CrudApiProvider.authKey = key;
    }
  }

  // STORAGE

  private getStorage(key: string): any {
    var x = localStorage.getItem(key);
    if (x) {
      return JSON.parse(x);
    } else {
      return undefined;
    }
  }

  private setStorage(key: string, value: any) {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private removeStorage(key: string) {
    localStorage.removeItem(key);
  }

  // FNS

  isUsernameAvailable(username: string) {
    return this.crudApi
      .get('/api/users', {
        username: String(username || '').toLowerCase(),
      })
      .then(
        res => {
          if (_.size(res.data) > 0) {
            return false;
          } else {
            return true;
          }
        },
        e => {
          this.log('error with getting username', e);
        }
      );
  }

  isEmailAddressAvailable(email: string) {
    return this.crudApi
      .get('/api/users', {
        email: email,
      })
      .then(
        res => {
          this.log('response', res.data.length);
          if (res.data.length > 0) {
            this.log('length');
            return false;
          } else {
            return true;
          }
        },
        e => {
          this.log('error with me', e);
        }
      );
  }

  private facebookErrorToast(e) {
    let s = e && e.message ? e.message : '',
      t = this.toastCtrl.create({
        message: 'There was an error logging into Facebook. ' + s,
        showCloseButton: true,
        closeButtonText: 'Complete Form',
      });
    t.present();
  }

  requestPasswordResetCode(usernameEmail: string) {
    const loader = this.loadingCtrl.create();

    loader.present();

    return this.crudApi.post('/turnkey/forgot', { email: usernameEmail }).then(
      res => {
        loader.dismiss();
      },
      e => {
        loader.dismiss();
        return Promise.reject(e);
      }
    );
  }

  submitPasswordReset(code: string, password: string, username: string) {
    const loader = this.loadingCtrl.create();

    loader.present();

    return this.crudApi
      .put('/turnkey/reset', {
        code: code,
        password: password,
      })
      .then(res => {
        if (username) {
          return this.defaultLogin(username, password, true);
        } else {
          return;
        }
      })
      .then(
        res => {
          loader.dismiss();
        },
        e => {
          loader.dismiss();
          return Promise.reject(e);
        }
      );
  }

  private setLoading() {
    if (!this.loader) {
      this.loader = this.loadingCtrl.create();
      this.loader.present();
    } else {
      this.loader.dismiss();
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth/auth.ts